import React from "react";
import {
    Box, Drawer, Divider, List, ListItem, ListItemButton,
    ListItemIcon, ListItemText, Typography, Collapse
} from "@mui/material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import ApartmentIcon from "@mui/icons-material/Apartment";
import PublicIcon from "@mui/icons-material/Public";
import SettingsIcon from "@mui/icons-material/Settings";
import CategoryIcon from "@mui/icons-material/Category";
import TuneIcon from "@mui/icons-material/Tune";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import HandshakeIcon from "@mui/icons-material/Handshake";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import DeliveryDiningIcon from "@mui/icons-material/DeliveryDining";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SportsTennisIcon from "@mui/icons-material/SportsTennis";
import MapIcon from "@mui/icons-material/Map";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";


import { getCurrentUser, hasAnyRole } from "../../utils/auth";

const drawerWidth = 280;

export default function Sidebar({ mobileOpen, onClose }) {
    const location = useLocation();
    const user = getCurrentUser();

    const propertyPaths = ["/admin/categories", "/admin/hotel-groups", "/admin/awards", "/admin/logos", "/admin/cuisines", "/admin/restaurant-prices", "/admin/delivery-services", "/admin/locations", "/admin/leisure-activities", "/admin/regions", "/admin/classifications", "/admin/amenities"];
    const isPropertiesSelected = propertyPaths.some((path) => location.pathname.startsWith(path));
    const [propertiesOpen, setPropertiesOpen] = React.useState(isPropertiesSelected);

    React.useEffect(() => {
        if (isPropertiesSelected) setPropertiesOpen(true);
    }, [isPropertiesSelected]);

    const items = [
        {
            label: "Live Seite",
            path: "/",
            icon: <PublicIcon />,
            requiredRoles: [], // für alle sichtbar
            selected: location.pathname === "/"
        },
        {
            label: "Dashboard",
            path: "/admin",
            icon: <DashboardIcon />,
            requiredRoles: [], // für alle sichtbar
            selected: location.pathname === "/admin" || location.pathname === "/admin/"
        },
        {
            label: "Nutzerverwaltung",
            path: "/admin/users",
            icon: <PeopleIcon />,
            requiredRoles: [],
            selected: location.pathname.startsWith("/admin/users")
        },
        {
            label: "Unternehmen",
            path: "/admin/companies",
            icon: <ApartmentIcon />,
            requiredRoles: [],
            selected: location.pathname.startsWith("/admin/companies")
        },
        {
            label: "Firmeneigenschaften",
            icon: <TuneIcon />,
            requiredRoles: [],
            selected: isPropertiesSelected,
            children: [
                {
                    label: "Kategorien",
                    path: "/admin/categories",
                    icon: <CategoryIcon />,
                    selected: location.pathname.startsWith("/admin/categories")
                },
                {
                    label: "Hotelgruppen",
                    path: "/admin/hotel-groups",
                    icon: <ApartmentIcon />,
                    selected: location.pathname.startsWith("/admin/hotel-groups")
                },
                {
                    label: "Auszeichnungen",
                    path: "/admin/awards",
                    icon: <EmojiEventsIcon />,
                    selected: location.pathname.startsWith("/admin/awards")
                },
                {
                    label: "Logos",
                    path: "/admin/logos",
                    icon: <LocalOfferOutlinedIcon />,
                    selected: location.pathname.startsWith("/admin/logos")
                },
                {
                    label: "Küchen",
                    path: "/admin/cuisines",
                    icon: <RestaurantIcon />,
                    selected: location.pathname.startsWith("/admin/cuisines")
                },
                {
                    label: "Preise",
                    path: "/admin/restaurant-prices",
                    icon: <LocalOfferIcon />,
                    selected: location.pathname.startsWith("/admin/restaurant-prices")
                },
                {
                    label: "Lieferservice",
                    path: "/admin/delivery-services",
                    icon: <DeliveryDiningIcon />,
                    selected: location.pathname.startsWith("/admin/delivery-services")
                },
                {
                    label: "Standorte / Umgebung",
                    path: "/admin/locations",
                    icon: <LocationOnIcon />,
                    selected: location.pathname.startsWith("/admin/locations")
                },
                {
                    label: "Freizeit & Sport",
                    path: "/admin/leisure-activities",
                    icon: <SportsTennisIcon />,
                    selected: location.pathname.startsWith("/admin/leisure-activities")
                },
                {
                    label: "Regionen",
                    path: "/admin/regions",
                    icon: <MapIcon />,
                    selected: location.pathname.startsWith("/admin/regions")
                },
                {
                    label: "Klassifikationen",
                    path: "/admin/classifications",
                    icon: <CategoryIcon />,
                    selected: location.pathname.startsWith("/admin/classifications")
                },
                {
                    label: "Ausstattungen",
                    path: "/admin/amenities",
                    icon: <CategoryIcon />,
                    selected: location.pathname.startsWith("/admin/amenities")
                },
            ],
        },
        {
            label: "Partner",
            path: "/admin/partners",
            icon: <HandshakeIcon />,
            requiredRoles: [],
            selected: location.pathname.startsWith("/admin/partners")
        },
        {
            label: "Beliebte Themen",
            path: "/admin/popular-topics",
            icon: <TravelExploreIcon />,
            requiredRoles: [],
            selected: location.pathname.startsWith("/admin/popular-topics")
        },
        {
            label: "HRI Vorteile",
            path: "/admin/hri-benefits",
            icon: <WorkspacePremiumIcon />,
            requiredRoles: [],
            selected: location.pathname.startsWith("/admin/hri-benefits")
        },
        {
            label: "Newsletter",
            path: "/admin/newsletter",
            icon: <MarkEmailReadIcon />,
            requiredRoles: [],
            selected: location.pathname.startsWith("/admin/newsletter")
        },
        {
            label: "Domain Verwaltung",
            path: "/admin/domains",
            icon: <SettingsIcon />,
            requiredRoles: [],
            selected: location.pathname.startsWith("/admin/domains")
        }
    ];

    const visibleItems = items.filter(i => hasAnyRole(user, i.requiredRoles));

    const content = (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <Box sx={{ p: 3, pb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                    component="img"
                    src={`${process.env.PUBLIC_URL}/images/favicon.png`}
                    alt="HRI Logo"
                    sx={{ width: 36, height: 36, borderRadius: 2 }}
                />
                <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '.2px' }}>
                    HRI Admin
                </Typography>
            </Box>
            <Divider />
            <List sx={{ p: 1 }}>
                {visibleItems.map(item => {
                    if (item.children) {
                        return (
                            <React.Fragment key={item.label}>
                                <ListItem disablePadding>
                                    <ListItemButton
                                        selected={item.selected}
                                        sx={{ borderRadius: 2 }}
                                        onClick={() => setPropertiesOpen((open) => !open)}
                                    >
                                        <ListItemIcon>{item.icon}</ListItemIcon>
                                        <ListItemText primary={item.label} />
                                        {propertiesOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </ListItemButton>
                                </ListItem>

                                <Collapse in={propertiesOpen} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding sx={{ pl: 2, py: 0.5 }}>
                                        {item.children.map((child) => (
                                            <ListItem key={child.path} disablePadding>
                                                <ListItemButton
                                                    component={RouterLink}
                                                    to={child.path}
                                                    selected={child.selected}
                                                    sx={{
                                                        borderRadius: 2,
                                                        pl: 2,
                                                        minHeight: 40,
                                                    }}
                                                    onClick={onClose}
                                                >
                                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                                        {child.icon}
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={child.label}
                                                        primaryTypographyProps={{ fontSize: 14 }}
                                                    />
                                                </ListItemButton>
                                            </ListItem>
                                        ))}
                                    </List>
                                </Collapse>
                            </React.Fragment>
                        );
                    }

                    return (
                        <ListItem key={item.path} disablePadding>
                            <ListItemButton
                                component={RouterLink}
                                to={item.path}
                                selected={item.selected}
                                sx={{ borderRadius: 2 }}
                                onClick={onClose}
                            >
                                <ListItemIcon>{item.icon}</ListItemIcon>
                                <ListItemText primary={item.label} />
                            </ListItemButton>
                        </ListItem>
                    );
                })}
            </List>
            <Box sx={{ flexGrow: 1 }} />
        </Box>
    );

    return (
        <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }} aria-label="sidebar">
            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={onClose}
                ModalProps={{ keepMounted: true }}
                sx={{ display: { xs: "block", md: "none" }, "& .MuiDrawer-paper": { boxSizing: "border-box", width: drawerWidth } }}
            >
                {content}
            </Drawer>
            <Drawer
                variant="permanent"
                sx={{ display: { xs: "none", md: "block" }, "& .MuiDrawer-paper": { boxSizing: "border-box", width: drawerWidth, borderRight: "1px solid rgba(2,14,41,.06)" } }}
                open
            >
                {content}
            </Drawer>
        </Box>
    );
}
